import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios, { AxiosError } from 'axios';
import { clearCsrfToken, loadCsrfToken } from '../services/session';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const toUser = (data: any): User => ({
  id: data.id,
  username: data.username,
  email: data.email ?? undefined,
  is_admin: data.is_admin ?? false,
  is_super_admin: data.is_super_admin ?? false,
  requires_approval: data.requires_approval ?? false,
  can_complete_po_line: data.can_complete_po_line ?? false,
  default_complete_po_yes: data.default_complete_po_yes ?? false,
  // Default both to true so if the backend ever omits these fields (older
  // deploy, downgraded response shape, etc.), existing users keep the
  // same mode visibility they had before the capability concept existed.
  // SSO users get the correct per-role values applied by the backend on
  // every login, so this fallback only matters for pre-feature accounts.
  can_invoice: data.can_invoice ?? true,
  can_voucher_req: data.can_voucher_req ?? true,
  customer_group: data.customer_group,
  account: data.account ?? null,
});

export interface AccountSettings {
  gl_format_regex?: string;
  gl_format_placeholder?: string;
  gl_format_label?: string;
  gl_format_example?: string;
  default_currency?: string;
  state_tax_code?: string;
  vendor_csv_format?: string;
  export_format?: string;
  [key: string]: unknown;
}

export interface Account {
  id: number;
  slug: string;
  name: string;
  settings: AccountSettings;
}

interface User {
  id: number;
  username: string;
  email?: string;
  is_admin?: boolean;
  is_super_admin?: boolean;
  requires_approval?: boolean;
  can_complete_po_line?: boolean;
  default_complete_po_yes?: boolean;
  // Capability flags driven by the KeyNet SSO roles claim (1/2/3). UI
  // uses these to hide the invoice/voucher toggle when the user only has
  // one mode available.
  can_invoice?: boolean;
  can_voucher_req?: boolean;
  customer_group?: string;
  account?: Account | null;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for an existing session on mount. Always hit /auth/me first so the
  // server (which prefers the __Host-ks_session cookie over any bearer token
  // in the Authorization header) is authoritative. This means a fresh KeyNet
  // SSO login overrides any stale break-glass user still cached in
  // localStorage from a previous session. Only fall back to the localStorage
  // snapshot when /auth/me fails outright (offline, backend down, etc.) so
  // the UI doesn't blank out on transient network blips.
  useEffect(() => {
    const checkAuth = async () => {
      const API_BASE_URL = process.env.REACT_APP_API_URL || '';
      const storedUser = localStorage.getItem('auth_user');
      const storedToken = localStorage.getItem('auth_token');
      try {
        const me = await axios.get(`${API_BASE_URL}/auth/me`, { withCredentials: true });
        await loadCsrfToken();
        setUser(toUser(me.data));
      } catch (error: any) {
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          // No live session on the server. Any cached break-glass creds are
          // stale (otherwise /auth/me would have accepted the bearer token).
          localStorage.removeItem('auth_user');
          localStorage.removeItem('auth_token');
          clearCsrfToken();
        } else if (storedUser && storedToken) {
          // Network error reaching /auth/me; keep the cached user visible so
          // the UI doesn't lose context on a transient outage.
          try {
            setUser(JSON.parse(storedUser));
          } catch {
            localStorage.removeItem('auth_user');
            localStorage.removeItem('auth_token');
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (username: string, password: string): Promise<void> => {
    // Validate input
    if (!username || !password) {
      throw new Error('Username and password are required');
    }

    // Clear any leftover impersonation state from a previous session.
    localStorage.removeItem('auth_token_original');
    localStorage.removeItem('auth_user_original');
    localStorage.removeItem('impersonating_account_name');

    try {
      // Make API call to auth/login endpoint
      const API_BASE_URL = process.env.REACT_APP_API_URL || '';
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        username,
        password,
      });

      // Extract token from response
      // Backend returns 'access_token' not 'token'
      const { access_token } = response.data;
      const token = access_token || response.data.token; // Support both formats
      
      if (!token) {
        throw new Error('No token received from server');
      }

      // Get user info - try to get from response or fetch from /auth/me
      let userData: User;
      if (response.data.user) {
        userData = response.data.user;
      } else {
        // If user data not in login response, fetch it
        const API_BASE_URL = process.env.REACT_APP_API_URL || '';
        const userResponse = await axios.get(`${API_BASE_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        userData = {
          id: userResponse.data.id,
          username: userResponse.data.username,
          email: userResponse.data.email,
          is_admin: userResponse.data.is_admin ?? false,
          is_super_admin: userResponse.data.is_super_admin ?? false,
          requires_approval: userResponse.data.requires_approval ?? false,
          can_complete_po_line: userResponse.data.can_complete_po_line ?? false,
          default_complete_po_yes: userResponse.data.default_complete_po_yes ?? false,
          can_invoice: userResponse.data.can_invoice ?? true,
          can_voucher_req: userResponse.data.can_voucher_req ?? true,
          customer_group: userResponse.data.customer_group,
          account: userResponse.data.account ?? null,
        };
      }

      // Store in localStorage
      localStorage.setItem('auth_user', JSON.stringify(userData));
      localStorage.setItem('auth_token', token);

      setUser(userData);
    } catch (error) {
      // Handle API errors
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<{ detail?: string }>;
        if (axiosError.response) {
          const status = axiosError.response.status;
          const message = axiosError.response.data?.detail || axiosError.message;
          
          if (status === 401) {
            throw new Error('Invalid username or password');
          } else if (status === 400) {
            throw new Error(message || 'Invalid request');
          } else {
            throw new Error(message || 'Login failed. Please try again.');
          }
        } else if (axiosError.request) {
          throw new Error('No response from server. Please check your connection.');
        } else {
          throw new Error(axiosError.message || 'An unexpected error occurred.');
        }
      } else {
        throw new Error(error instanceof Error ? error.message : 'Login failed. Please try again.');
      }
    }
  };

  const logout = () => {
    if (!localStorage.getItem('auth_token')) {
      // KeyNet session: revoke it server-side (the CSRF token is attached by
      // the interceptor). Clear the CSRF token only after the POST settles,
      // otherwise the interceptor reads a null token and the backend rejects
      // the logout with 403.
      const API_BASE_URL = process.env.REACT_APP_API_URL || '';
      axios
        .post(`${API_BASE_URL}/auth/logout`, null, { withCredentials: true })
        .catch(() => undefined)
        .finally(() => clearCsrfToken());
    }
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_token');
    // Drop any impersonation state — banner shouldn't survive across logins.
    localStorage.removeItem('auth_token_original');
    localStorage.removeItem('auth_user_original');
    localStorage.removeItem('impersonating_account_name');
    localStorage.removeItem('ocr-files');
    sessionStorage.removeItem('activeJobIds');
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    isAdmin: user?.is_admin || false,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

